import React from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API, resolveUrl, devDebug, useViz } from "./visualizerShared";

export const Top6Overlay = () => {
  const {
    top6Visible, top6Entries, top6TitleText, fontTitle, fontCountry, getActiveFont,
    top6StyleCustom, top6FontTitle, top6FontCountry,
    overlay12BgStyle, overlay12BgOpacity, overlay12BgColor, overlay12UseAdminColors,
    primaryColor, secondaryColor, tertiaryColor,
  } = useViz();

  // Per-overlay custom fonts (fall back to the global admin fonts)
  const titleFont = top6StyleCustom ? top6FontTitle : fontTitle;
  const countryFont = top6StyleCustom ? top6FontCountry : fontCountry;

  // Same background logic as the 12-points overlay (shared config)
  const bgOpacityRatio = Math.max(0, Math.min(1, overlay12BgOpacity / 100));
  let bgStyle = {};
  if (overlay12BgStyle === "gradient") {
    const c1 = overlay12UseAdminColors ? primaryColor : "#EC4899";
    const c2 = overlay12UseAdminColors ? tertiaryColor : "#8B5CF6";
    const c3 = overlay12UseAdminColors ? secondaryColor : "#22D3EE";
    bgStyle = {
      background: `radial-gradient(ellipse at 30% 30%, ${c1}${Math.round(bgOpacityRatio * 153).toString(16).padStart(2,'0')} 0%, transparent 60%), radial-gradient(ellipse at 70% 70%, ${c3}${Math.round(bgOpacityRatio * 153).toString(16).padStart(2,'0')} 0%, transparent 60%), linear-gradient(135deg, ${c2}${Math.round(bgOpacityRatio * 230).toString(16).padStart(2,'0')} 0%, #000000${Math.round(bgOpacityRatio * 255).toString(16).padStart(2,'0')} 100%)`,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)'
    };
  } else if (overlay12BgStyle === "solid") {
    const hex = overlay12BgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    bgStyle = {
      background: `rgba(${r}, ${g}, ${b}, ${bgOpacityRatio})`,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)'
    };
  } else {
    bgStyle = {
      background: `rgba(0, 0, 0, ${bgOpacityRatio * 0.3})`,
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)'
    };
  }

  return (
    <AnimatePresence>
      {top6Visible && top6Entries.length > 0 && (
        <motion.div
          key="top6-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="fixed inset-0 z-[9998] flex items-center justify-center"
          style={bgStyle}
          data-testid="top6-overlay"
        >
          <button
            onClick={async () => {
              try { await axios.post(`${API}/admin/hide-top6-overlay`); } catch (e) { devDebug("No se pudo ocultar el overlay top6:", e?.message); }
            }}
            className="absolute top-6 right-6 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm backdrop-blur-md border border-white/20 transition-colors"
            data-testid="btn-close-top6-overlay"
          >
            Cerrar
          </button>

          <div className="flex flex-col items-center px-12 w-full max-w-5xl">
            <motion.h2
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-white font-medium tracking-[0.15em] border-b border-white/80 pb-3 px-10 mb-8 text-center whitespace-nowrap"
              style={{
                fontFamily: `${getActiveFont(titleFont, "title")}, Inter, Arial, sans-serif`,
                fontSize: "clamp(32px, min(4.5vw, 8vh), 72px)",
                textShadow: "0 0 30px rgba(255,255,255,0.55)"
              }}
              data-testid="top6-title"
            >
              {top6TitleText}
            </motion.h2>

            <div className="flex flex-col items-start gap-2 mx-auto">
              {top6Entries.map((entry, i) => {
                const total = top6Entries.length;
                const delay = 0.5 + (total - 1 - i) * 0.7;
                const fontSize = `clamp(${24 + (total - 1 - i) * 2}px, min(${5.0 - i * 0.3}vw, ${8.2 - i * 0.5}vh), ${74 - i * 5}px)`;
                const flagSize = `clamp(${28 + (total - 1 - i) * 2}px, min(${3.2 - i * 0.2}vw, ${5.6 - i * 0.35}vh), ${54 - i * 3}px)`;
                return (
                  <motion.div
                    key={entry.country_id}
                    initial={{ opacity: 0, x: -40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-4"
                    style={{ textShadow: "0 0 24px rgba(255,255,255,0.45)" }}
                    data-testid={`top6-row-${entry.position}`}
                  >
                    <span
                      className="text-white font-medium tabular-nums text-right shrink-0"
                      style={{ fontFamily: `${getActiveFont(countryFont, "country")}, Inter, Arial, sans-serif`, fontSize, width: "clamp(64px, 5.5vw, 110px)" }}
                    >
                      {entry.position}<span style={{ fontFamily: "Inter, Arial, sans-serif" }}>.</span>
                    </span>
                    {entry.flag_text ? (
                      <span
                        className="text-white font-black shrink-0"
                        style={{ fontSize: flagSize, fontFamily: `${getActiveFont(titleFont, "title")}, Inter, Arial, sans-serif` }}
                      >
                        {entry.flag_text}
                      </span>
                    ) : entry.flag_url ? (
                      <img
                        src={resolveUrl(entry.flag_url)}
                        alt={entry.name}
                        className="object-contain shrink-0 rounded-sm"
                        style={{ height: flagSize, width: "auto", filter: "drop-shadow(0 0 10px rgba(255,255,255,0.3))" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : null}
                    <span
                      className="text-white font-medium tracking-wide whitespace-nowrap"
                      style={{ fontFamily: `${getActiveFont(countryFont, "country")}, Inter, Arial, sans-serif`, fontSize }}
                    >
                      {entry.name}: {entry.points} p<span style={{ fontFamily: "Inter, Arial, sans-serif" }}>.</span>
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
